import React, { useEffect } from "react";
import { To } from "react-router";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import PermissionErrorBase from "common/components/PermissionErrorBase";
import { useGetUser, useGetSignalAuthorization } from "common/hooks/gqlHooks";
import { Env, EnvTypes } from "constants/base";
import { useAuth } from "hooks/useAuth";
import { SignalUser, User } from "gql/graphql";

class ProtectedErrorObjectError extends Error {
  constructor(message: string | undefined) {
    super(message)
    this.name = "ProtectedErrorObjectError"
  }
}

type ChildrenProps = {
  permissionErrorCustom?: React.FC
  children?: React.ReactElement
  env?: EnvTypes
  redirect?: To
}

const adminRole = ["Developer", "Admin"] as const
type AdminRole = (typeof adminRole)[number]

const companyRole = ["CompanyUser", "CompanyAdmin"] as const
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type CompanyRole = (typeof companyRole)[number] // NOTE: default role is CompanyUser

const companyAdminRole = ["CompanyAdmin"] as const
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type CompanyAdminRole = (typeof companyAdminRole)[number] // NOTE: CompanyAdmin can edit company's info, user, etc.

function hasAdminRole(roles: string[]): boolean {
  return roles.some((role) => adminRole.includes(role as AdminRole))
}

/**
 * Protected component.
 * @param param0 children component or components
 * @returns redirect to login page if not authenticated or render children
 *          if authenticated or show 404 page if user is not has permission to access to the page.
 */
export const Protected: React.FC<ChildrenProps> = ({
  permissionErrorCustom,
  children,
  env,
  redirect,
}: ChildrenProps) => {
  // redirect to login page if not authenticated
  const {
    authStatus,
    isLoading,
    user,
    setUser,
    setSignalUser
  } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()


  const { data: userData, fetching: userDataFetching, error: userDataError, fetchUser } = useGetUser();
  useEffect(() => {
    if (authStatus === "authenticated") fetchUser();
  }, [authStatus, fetchUser]);
  useEffect(() => {
    if (userData && userData.getUser) {
      setUser({ ...userData.getUser as User });
    }
  }, [userData, setUser]);
  const {
    data: signalUserData, fetching: signalUserDataFetching, error: signalUserDataError, fetchSignalAuthorization
  } = useGetSignalAuthorization();
  useEffect(() => {
    if (authStatus === "authenticated") fetchSignalAuthorization();
  }, [authStatus, fetchSignalAuthorization]);
  useEffect(() => {
    if (signalUserData && signalUserData.getSignalAuthorization) {
      setSignalUser({ ...signalUserData.getSignalAuthorization as SignalUser });
    }
  }, [signalUserData, setSignalUser]);

  useEffect(() => {
    if (authStatus !== "authenticated") return;
    // usersはpostConfirmationでレコードが作成されるが、初回ログインでユーザー入力項目が値無しの場合、
    // company_nameは画面からの必須項目の一つなので、この判定でユーザーデータ登録済みかどうかを判断する
    if (user && !user.company_name) {
      navigate("/initialize/user_data", { replace: true });
    }
  }, [authStatus, user]);

  if (isLoading) return <></>;

  // if env is not match to running env, redirect to login page.
  if (!!env && env !== Env) {
    if(!!redirect)
      return <Navigate replace to={redirect} />
  }

  if (permissionErrorCustom && typeof permissionErrorCustom !== "function")
    throw new ProtectedErrorObjectError(
      "Error: permissionErrorCustom is not function.",
    )

  // get login state from useAuth

  // check current location (if auth page, do not redirect to login page)
  const authPageRegex = /^\/auth\/.*/
  const isAuthPage = authPageRegex.test(location.pathname)

  // render children if authenticated
  if (authStatus === "authenticated") {
    return <>{children}</>
  }

  // or redirect to login page if not authenticated
  if (!isAuthPage) {
    return <Navigate replace to="/auth/signIn"/>
  }

  // or show permission error page if user is not has permission to access to the page.
  if (!permissionErrorCustom) return <PermissionErrorBase />
  return <>{React.createElement(permissionErrorCustom)}</>
}
